export interface FbxEmbeddedBinaryContent {
  kind: 'binary';
  bytes: Uint8Array;
}

export interface FbxEmbeddedBase64Content {
  kind: 'base64';
  data: string;
}

export type FbxEmbeddedContent =
  | FbxEmbeddedBinaryContent
  | FbxEmbeddedBase64Content;

export interface ParsedFbxMaterialRecord {
  id: number;
  name: string;
}

export interface ParsedFbxTextureRecord {
  id: number;
  name: string;
  fileName: string | null;
  relativeFileName: string | null;
}

export interface ParsedFbxVideoRecord {
  id: number;
  name: string;
  fileName: string | null;
  relativeFileName: string | null;
  content: FbxEmbeddedContent | null;
}

export interface ParsedFbxConnectionRecord {
  childId: number;
  parentId: number;
  relationship: string;
}

export interface FbxMaterialTextureTile {
  textureId: number;
  videoId: number | null;
  fileName: string | null;
  relativeFileName: string | null;
  tile: number | null;
  u: number | null;
  v: number | null;
  usesUdimToken: boolean;
  content: FbxEmbeddedContent | null;
  mimeType: string | null;
}

export interface FbxMaterialTextureSet {
  materialId: number;
  materialName: string;
  channel: string;
  textureIds: number[];
  tiles: FbxMaterialTextureTile[];
  isUdim: boolean;
}

export interface UdimGridPosition {
  tile: number;
  u: number;
  v: number;
}

export interface UdimAtlasLayout {
  columns: number;
  rows: number;
  minU: number;
  maxU: number;
  minV: number;
  maxV: number;
  offsetX: number;
  offsetY: number;
  repeatX: number;
  repeatY: number;
  missingTiles: number[];
}

function getBasename(path: string): string {
  const parts = path.split(/[\\/]/);
  return parts[parts.length - 1] ?? path;
}

export function hasUdimToken(path: string | null | undefined): boolean {
  if (!path) return false;
  return /<UDIM>/i.test(path);
}

export function extractUdimTileNumber(
  path: string | null | undefined,
): number | null {
  if (!path) return null;

  const basename = getBasename(path);
  const explicitTile = basename.match(/(?:^|[._-])(1\d{3})(?=[._-]|$)/);
  if (explicitTile) {
    return Number(explicitTile[1]);
  }

  const uvTile = basename.match(/(?:^|[._-])u(\d+)[_-]v(\d+)(?=[._-]|$)/i);
  if (!uvTile) return null;

  const rawU = Number(uvTile[1]);
  const rawV = Number(uvTile[2]);
  if (!Number.isFinite(rawU) || !Number.isFinite(rawV)) return null;

  // Most DCCs export u1_v1-style labels for tile 1001, but some pipelines
  // still use zero-based labels. Prefer the 1-based convention unless either
  // axis is explicitly zero.
  const u = rawU === 0 || rawV === 0 ? rawU : rawU - 1;
  const v = rawU === 0 || rawV === 0 ? rawV : rawV - 1;
  return udimGridToTileNumber(u, v);
}

export function udimGridToTileNumber(u: number, v: number): number {
  return 1001 + u + v * 10;
}

export function udimTileToGrid(tile: number): UdimGridPosition | null {
  if (!Number.isFinite(tile) || tile < 1001) return null;
  const offset = tile - 1001;
  return {
    tile,
    u: offset % 10,
    v: Math.floor(offset / 10),
  };
}

export function buildUdimAtlasLayout(
  tileNumbers: readonly number[],
): UdimAtlasLayout | null {
  if (tileNumbers.length === 0) return null;

  const positions = tileNumbers
    .map((tile) => udimTileToGrid(tile))
    .filter((value): value is UdimGridPosition => value !== null);
  if (positions.length === 0) return null;

  const uValues = positions.map((position) => position.u);
  const vValues = positions.map((position) => position.v);
  const minU = Math.min(...uValues);
  const maxU = Math.max(...uValues);
  const minV = Math.min(...vValues);
  const maxV = Math.max(...vValues);
  const columns = maxU - minU + 1;
  const rows = maxV - minV + 1;
  const offsetX = minU === 0 ? 0 : -minU / columns;
  const offsetY = minV === 0 ? 0 : -minV / rows;

  const presentTiles = new Set(tileNumbers);
  const missingTiles: number[] = [];
  for (let v = minV; v <= maxV; v += 1) {
    for (let u = minU; u <= maxU; u += 1) {
      const tile = udimGridToTileNumber(u, v);
      if (!presentTiles.has(tile)) {
        missingTiles.push(tile);
      }
    }
  }

  return {
    columns,
    rows,
    minU,
    maxU,
    minV,
    maxV,
    offsetX,
    offsetY,
    repeatX: 1 / columns,
    repeatY: 1 / rows,
    missingTiles,
  };
}

export function getEmbeddedTextureMimeType(
  path: string | null | undefined,
): string | null {
  if (!path) return null;

  const extension = getBasename(path)
    .slice(getBasename(path).lastIndexOf('.') + 1)
    .toLowerCase();
  switch (extension) {
    case 'bmp':
      return 'image/bmp';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    case 'gif':
      return 'image/gif';
    case 'tif':
    case 'tiff':
      return 'image/tiff';
    case 'tga':
      return 'image/x-tga';
    default:
      return null;
  }
}

function createTextureTile(
  texture: ParsedFbxTextureRecord,
  video: ParsedFbxVideoRecord | null,
): FbxMaterialTextureTile {
  const fileName = texture.fileName ?? video?.fileName ?? null;
  const relativeFileName =
    texture.relativeFileName ?? video?.relativeFileName ?? null;
  const candidatePaths = [
    relativeFileName,
    fileName,
    video?.relativeFileName ?? null,
    video?.fileName ?? null,
  ];
  const tile =
    candidatePaths
      .map((candidatePath) => extractUdimTileNumber(candidatePath))
      .find((value): value is number => value !== null) ?? null;
  const position = tile !== null ? udimTileToGrid(tile) : null;

  return {
    textureId: texture.id,
    videoId: video?.id ?? null,
    fileName,
    relativeFileName,
    tile,
    u: position?.u ?? null,
    v: position?.v ?? null,
    usesUdimToken: candidatePaths.some((candidatePath) =>
      hasUdimToken(candidatePath),
    ),
    content: video?.content ?? null,
    mimeType: getEmbeddedTextureMimeType(relativeFileName ?? fileName),
  };
}

function isUdimTextureSet(tiles: readonly FbxMaterialTextureTile[]): boolean {
  if (tiles.length > 1) return true;

  return tiles.some(
    (tile) => tile.usesUdimToken || (tile.tile !== null && tile.tile !== 1001),
  );
}

export function buildFbxMaterialTextureSets(
  materials: ReadonlyMap<number, ParsedFbxMaterialRecord>,
  textures: ReadonlyMap<number, ParsedFbxTextureRecord>,
  videos: ReadonlyMap<number, ParsedFbxVideoRecord>,
  connections: readonly ParsedFbxConnectionRecord[],
): FbxMaterialTextureSet[] {
  const childrenByParent = new Map<number, ParsedFbxConnectionRecord[]>();
  for (const connection of connections) {
    const entries = childrenByParent.get(connection.parentId) ?? [];
    entries.push(connection);
    childrenByParent.set(connection.parentId, entries);
  }

  const groups = new Map<string, FbxMaterialTextureSet>();
  for (const connection of connections) {
    if (!connection.relationship) continue;

    const material = materials.get(connection.parentId);
    const texture = textures.get(connection.childId);
    if (!material || !texture) continue;

    const key = `${material.id}:${connection.relationship}`;
    const group =
      groups.get(key) ??
      ({
        materialId: material.id,
        materialName: material.name,
        channel: connection.relationship,
        textureIds: [],
        tiles: [],
        isUdim: false,
      } satisfies FbxMaterialTextureSet);

    if (!group.textureIds.includes(texture.id)) {
      group.textureIds.push(texture.id);
    }

    const videoConnections = (childrenByParent.get(texture.id) ?? []).filter(
      (child) => videos.has(child.childId),
    );
    if (videoConnections.length === 0) {
      group.tiles.push(createTextureTile(texture, null));
    } else {
      for (const videoConnection of videoConnections) {
        const video = videos.get(videoConnection.childId);
        if (!video) continue;
        group.tiles.push(createTextureTile(texture, video));
      }
    }

    groups.set(key, group);
  }

  return [...groups.values()]
    .map((group) => ({
      ...group,
      tiles: [...group.tiles].sort((a, b) => {
        const aTile = a.tile ?? Number.MAX_SAFE_INTEGER;
        const bTile = b.tile ?? Number.MAX_SAFE_INTEGER;
        if (aTile !== bTile) return aTile - bTile;
        return (a.relativeFileName ?? a.fileName ?? '').localeCompare(
          b.relativeFileName ?? b.fileName ?? '',
        );
      }),
      textureIds: [...group.textureIds].sort((a, b) => a - b),
      isUdim: isUdimTextureSet(group.tiles),
    }))
    .sort((a, b) => {
      const nameCompare = a.materialName.localeCompare(b.materialName);
      if (nameCompare !== 0) return nameCompare;
      return a.channel.localeCompare(b.channel);
    });
}
